/**
 * Aurelia Size Guide (product dialog)
 *
 * Extracted from the inline <script> blocks that used to be duplicated in
 * snippets/variant-main-picker.liquid and blocks/as-bundle-buy-box.liquid.
 * As an external, deferred asset it is downloaded once, cached by the browser
 * across every product page and no longer blocks HTML parsing.
 *
 * Each [data-asg-dialog-content] element needs:
 *   - data-market-country="{{ localization.country.iso_code }}"
 *   - a child <script type="application/json" data-asg-product-context>
 */
(() => {
  if (window.AureliaSizeGuide) return;

  const initSizeGuide = (dialogContent) => {
    if (!(dialogContent instanceof Element)) return;
    const root = dialogContent.querySelector('[data-asg-root]');
    const contextNode = dialogContent.querySelector('[data-asg-product-context]');

    if (!root || root.dataset.asgInitialized === 'true') return;
    root.dataset.asgInitialized = 'true';

    let productContext = {};
    try {
      productContext = JSON.parse(contextNode?.textContent || '{}');
    } catch (error) {
      console.warn('Aurelia Size Guide: product context could not be read.', error);
    }

    const typeSelect = root.querySelector('[data-asg-product-type]');
    const typeField = root.querySelector('[data-asg-type-field]');
    const fitField = root.querySelector('[data-asg-fit-field]');
    const fitSelect = root.querySelector('[data-asg-fit]');
    const contextBox = root.querySelector('[data-asg-context]');
    const productTitle = root.querySelector('[data-asg-product-title]');
    const productSummary = root.querySelector('[data-asg-product-summary]');
    const productPill = root.querySelector('[data-asg-product-pill]');
    const changeTypeButton = root.querySelector('[data-asg-change-type]');
    const recommendButton = root.querySelector('[data-asg-recommend]');
    const resetButton = root.querySelector('[data-asg-reset]');
    const warningBox = root.querySelector('[data-asg-warning]');
    const resultBox = root.querySelector('[data-asg-result]');
    const resultSize = root.querySelector('[data-asg-result-size]');
    const resultDetails = root.querySelector('[data-asg-result-details]');
    const resultReason = root.querySelector('[data-asg-result-reason]');
    const resultNote = root.querySelector('[data-asg-result-note]');
    const unitButtons = [...root.querySelectorAll('[data-asg-unit]')];
    const unitLabels = [...root.querySelectorAll('[data-asg-unit-label]')];

    // The page content predates footwear support. Add the option and field here so
    // the same Size Guide page can remain unchanged in Shopify's page editor.
    if (typeSelect && !typeSelect.querySelector('option[value="footwear"]')) {
      const footwearOption = document.createElement('option');
      footwearOption.value = 'footwear';
      footwearOption.textContent = 'Shoes & footwear';
      const hatsOption = typeSelect.querySelector('option[value="sun_hats"]');
      typeSelect.insertBefore(footwearOption, hatsOption || null);
    }

    const measurementGrid = root.querySelector('[data-measure-field="bust"]')?.parentElement;
    if (measurementGrid && !root.querySelector('[data-measure-field="foot_length"]')) {
      measurementGrid.insertAdjacentHTML('beforeend', `
        <div hidden data-measure-field="foot_length" class="asg-field">
          <label for="asg-foot-length">
            Foot length <span data-asg-unit-label>(cm)</span> <span class="asg-required">*</span>
          </label>
          <input
            id="asg-foot-length"
            data-measure="foot_length"
            type="number"
            min="0"
            step="0.1"
            inputmode="decimal"
            placeholder="e.g. 24.1"
          >
          <small>Measure both feet from the back of the heel to the longest toe and enter the longer measurement.</small>
        </div>
      `);
    }

    const measureFields = [...root.querySelectorAll('[data-measure-field]')];
    const measureInputs = [...root.querySelectorAll('[data-measure]')];

    const typeConfig = {
      tops: {
        label: 'Tops, shirts & pullovers',
        summary: 'Bust determines the size; height helps estimate coverage.',
        required: ['bust'],
        optional: ['height'],
        primary: ['bust']
      },
      jackets: {
        label: 'Jackets & zip layers',
        summary: 'Bust and waist matter, especially when layering.',
        required: ['bust', 'waist'],
        optional: ['height'],
        primary: ['bust', 'waist']
      },
      dresses: {
        label: 'Dresses & tunics',
        summary: 'Bust, waist and hips are combined for the best fit.',
        required: ['bust', 'waist', 'hips'],
        optional: ['height'],
        primary: ['bust', 'waist', 'hips']
      },
      pants: {
        label: 'Pants & shorts',
        summary: 'Waist and hips determine the size; inside leg helps with length.',
        required: ['waist', 'hips'],
        optional: ['inseam'],
        primary: ['waist', 'hips']
      },
      leggings: {
        label: 'Leggings',
        summary: 'Waist and hips determine the size and compression.',
        required: ['waist', 'hips'],
        optional: [],
        primary: ['waist', 'hips']
      },
      swimwear: {
        label: 'Swimwear',
        summary: 'Bust, waist and hips determine the size; height helps with torso length.',
        required: ['bust', 'waist', 'hips'],
        optional: ['height'],
        primary: ['bust', 'waist', 'hips']
      },
      footwear: {
        label: 'Shoes & footwear',
        summary: 'Foot length determines the shoe size. Measure both feet and use the longer measurement.',
        required: ['foot_length'],
        optional: [],
        primary: ['foot_length'],
        footwear: true
      },
      sun_hats: {
        label: 'Sun hats & visors',
        summary: 'Only head circumference is needed.',
        required: ['head'],
        optional: [],
        primary: ['head'],
        accessory: true
      },
      arm_sleeves: {
        label: 'Arm sleeves',
        summary: 'Only the circumference of the fullest part of the upper arm is needed.',
        required: ['bicep'],
        optional: [],
        primary: ['bicep'],
        accessory: true
      },
      gloves: {
        label: 'Sun gloves',
        summary: 'Only palm circumference is needed.',
        required: ['palm'],
        optional: [],
        primary: ['palm'],
        accessory: true
      },
      face_neck: {
        label: 'Face & neck accessories',
        summary: 'This product is normally One Size or adjustable.',
        required: [],
        optional: [],
        primary: [],
        oneSize: true
      },
      one_size_clothing: {
        label: 'One-size clothing',
        summary: 'This garment uses one flexible size; check its dimensions and coverage.',
        required: [],
        optional: [],
        primary: [],
        oneSize: true
      },
      no_size: {
        label: 'No size required',
        summary: 'No body measurements are required for this product.',
        required: [],
        optional: [],
        primary: [],
        noSize: true
      }
    };

    const auChart = [
      { au: 4, bust: 80, waist: 62, hips: 90 },
      { au: 6, bust: 85, waist: 67, hips: 95 },
      { au: 8, bust: 90, waist: 72, hips: 100 },
      { au: 10, bust: 95, waist: 77, hips: 105 },
      { au: 12, bust: 100, waist: 82, hips: 110 },
      { au: 14, bust: 105, waist: 87, hips: 115 },
      { au: 16, bust: 110, waist: 92, hips: 120 },
      { au: 18, bust: 115, waist: 97, hips: 125 },
      { au: 20, bust: 120, waist: 102, hips: 130 },
      { au: 22, bust: 125, waist: 107, hips: 135 },
      { au: 24, bust: 130, waist: 112, hips: 140 }
    ];

    // Generic AU/US women's footwear fallback. A product-specific metafield
    // (custom.fit_finder_shoe_chart) overrides this chart when supplied.
    const defaultFootwearChart = [
      { size: 'AU 4', maxFootLengthCm: 20.8 },
      { size: 'AU 4.5', maxFootLengthCm: 21.3 },
      { size: 'AU 5', maxFootLengthCm: 21.6 },
      { size: 'AU 5.5', maxFootLengthCm: 22.2 },
      { size: 'AU 6', maxFootLengthCm: 22.5 },
      { size: 'AU 6.5', maxFootLengthCm: 23.0 },
      { size: 'AU 7', maxFootLengthCm: 23.5 },
      { size: 'AU 7.5', maxFootLengthCm: 23.8 },
      { size: 'AU 8', maxFootLengthCm: 24.1 },
      { size: 'AU 8.5', maxFootLengthCm: 24.6 },
      { size: 'AU 9', maxFootLengthCm: 25.1 },
      { size: 'AU 9.5', maxFootLengthCm: 25.4 },
      { size: 'AU 10', maxFootLengthCm: 25.8 },
      { size: 'AU 10.5', maxFootLengthCm: 26.3 },
      { size: 'AU 11', maxFootLengthCm: 26.5 },
      { size: 'AU 11.5', maxFootLengthCm: 27.0 },
      { size: 'AU 12', maxFootLengthCm: 27.6 }
    ];

    const measurementLabels = {
      bust: 'Bust',
      waist: 'Waist',
      hips: 'Hips',
      height: 'Height',
      inseam: 'Inside leg',
      head: 'Head',
      bicep: 'Bicep',
      palm: 'Palm',
      foot_length: 'Foot length'
    };

    let currentUnit = 'cm';
    let currentType = '';
    const marketCountry = String(dialogContent.dataset.marketCountry || '');

    const normalize = (value) =>
      String(value || '')
        .toLowerCase()
        .replace(/&amp;/g, 'and')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();

    const buildContextSearchText = (context) => normalize([
      context.title,
      context.handle,
      context.shopifyProductType,
      ...(context.tags || []),
      ...(context.collections || [])
    ].join(' '));

    let contextSearchText = buildContextSearchText(productContext);

    const containsAny = (terms) => terms.some((term) => contextSearchText.includes(term));

    const detectProductType = () => {
      if (containsAny(['gift card', 'umbrella', 'parasol'])) return 'no_size';
      if (containsAny([
        'water shoe', 'water shoes', 'aqua shoe', 'aqua shoes', 'swim shoe', 'swim shoes',
        'beach shoe', 'beach shoes', 'wading shoe', 'wading shoes', 'barefoot shoe',
        'shoe', 'shoes', 'footwear', 'sneaker', 'sneakers', 'trainer', 'trainers',
        'sandal', 'sandals', 'slipper', 'slippers', 'boot', 'boots', 'loafer', 'loafers',
        'heel', 'heels', 'thong', 'thongs', 'flip flop', 'flip flops'
      ])) return 'footwear';
      if (containsAny(['arm sleeve', 'sun sleeve', 'uv sleeve', 'sleeves'])) return 'arm_sleeves';
      if (containsAny(['sun glove', 'uv glove', 'driving glove', 'gloves'])) return 'gloves';
      if (containsAny(['sun hat', 'bucket hat', 'wide brim', 'visor', 'sun visor', 'cap'])) return 'sun_hats';
      if (containsAny(['neck gaiter', 'face cover', 'face mask', 'neck cover', 'scarf', 'balaclava'])) return 'face_neck';
      if (containsAny(['swimsuit', 'swimwear', 'swim dress', 'rash guard', 'rashie', 'bikini', 'one piece'])) return 'swimwear';
      if (containsAny(['leggings', 'legging', 'tights'])) return 'leggings';
      if (containsAny(['trousers', 'pants', 'shorts', 'skort', 'culotte'])) return 'pants';
      if (containsAny(['dress', 'tunic'])) return 'dresses';
      if (containsAny(['jacket', 'zip layer', 'zip top', 'hoodie', 'cardigan', 'outerwear'])) return 'jackets';
      if (containsAny(['poncho', 'kaftan', 'sarong', 'one size clothing'])) return 'one_size_clothing';
      if (containsAny(['shirt', 'top', 'pullover', 'polo', 'blouse', 'tee', 't shirt'])) return 'tops';
      return 'tops';
    };

    const formatMarketSize = (value, productType = currentType || detectProductType()) => {
      const raw = String(value || '').trim();
      const match = raw.match(/^AU\s*(-?\d+(?:\.\d+)?)(?:\s*[-–—]\s*(-?\d+(?:\.\d+)?))?$/i);
      if (!match || marketCountry === 'AU') return raw;

      const footwear = productType === 'footwear';
      let prefix = 'AU';
      let offset = 0;

      if (marketCountry === 'GB') {
        prefix = 'UK';
        offset = footwear ? -2 : 0;
      } else if (marketCountry === 'US' || marketCountry === 'NZ' || marketCountry === 'SG') {
        prefix = 'US';
        offset = footwear ? 0 : -4;
      } else {
        return raw;
      }

      const cleanNumber = (number) => {
        const rounded = Math.round(number * 10) / 10;
        return Number.isInteger(rounded) ? String(rounded) : String(rounded);
      };

      const first = cleanNumber(Number.parseFloat(match[1]) + offset);
      const second = match[2] ? cleanNumber(Number.parseFloat(match[2]) + offset) : '';
      return `${prefix} ${first}${second ? `–${second}` : ''}`;
    };

    const localizeStaticSizeGuide = () => {
      if (marketCountry === 'AU') return;

      const bodyChartPrefix = marketCountry === 'GB' ? 'UK' : (marketCountry === 'US' || marketCountry === 'NZ' || marketCountry === 'SG' ? 'US' : 'AU');
      if (bodyChartPrefix === 'AU') return;

      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      let node;

      while ((node = walker.nextNode())) {
        const parent = node.parentElement;
        if (!parent || parent.closest('script, style')) continue;

        let text = node.nodeValue || '';
        text = text.replace(/Australian clothing size chart/gi, `${bodyChartPrefix} clothing size chart`);
        text = text.replace(/AU body chart/gi, `${bodyChartPrefix} body chart`);
        text = text.replace(/AU size/gi, `${bodyChartPrefix} size`);
        text = text.replace(/\bAU\s+(\d+(?:\.\d+)?)(?:\s*[-–—]\s*(\d+(?:\.\d+)?))?/g, (match, first, second) => {
          return formatMarketSize(`AU ${first}${second ? `–${second}` : ''}`, 'tops');
        });

        node.nodeValue = text;
      }
    };

    const toCm = (value) => currentUnit === 'in' ? value * 2.54 : value;
    const fromCm = (value) => currentUnit === 'in' ? value / 2.54 : value;
    const formatValue = (value) => {
      const rounded = Math.round(value * 10) / 10;
      return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
    };

    const showWarning = (message) => {
      warningBox.textContent = message;
      warningBox.classList.add('is-visible');
      resultBox.classList.remove('is-visible');
    };

    const clearMessages = () => {
      warningBox.textContent = '';
      warningBox.classList.remove('is-visible');
      resultBox.classList.remove('is-visible');
    };

    const updateContext = (type, automatic = false) => {
      const config = typeConfig[type];
      if (!config) return;

      if (productTitle) productTitle.textContent = productContext.title || 'This product';
      if (productSummary) productSummary.textContent = config.summary;
      if (productPill) {
        productPill.textContent = automatic ? 'Auto-detected product type' : 'Selected product type';
      }
      contextBox?.classList.add('is-visible');
    };

    const applyType = (type, { automatic = false } = {}) => {
      currentType = type;
      const config = typeConfig[type];

      measureFields.forEach((field) => {
        field.hidden = true;
        const input = field.querySelector('[data-measure]');
        if (input) input.required = false;
      });

      if (!config) {
        contextBox?.classList.remove('is-visible');
        fitField.hidden = false;
        clearMessages();
        return;
      }

      [...config.required, ...config.optional].forEach((measurement) => {
        const field = root.querySelector(`[data-measure-field="${measurement}"]`);
        const input = root.querySelector(`[data-measure="${measurement}"]`);
        if (field) field.hidden = false;
        if (input) input.required = config.required.includes(measurement);
      });

      fitField.hidden = Boolean(config.accessory || config.footwear || config.oneSize || config.noSize);
      updateContext(type, automatic);
      clearMessages();
    };

    let availableSizes = (productContext.availableSizes || []).map((size) => String(size).trim());

    const normalizeSize = (value) =>
      String(value || '')
        .toUpperCase()
        .replace(/AUSTRALIAN|AU SIZE|SIZE|AU/g, '')
        .replace(/\s+/g, '')
        .replace(/^0+/, '');

    const matchAvailableSize = (auSize) => {
      if (!availableSizes.length) return `AU ${auSize}`;

      const exact = availableSizes.find((size) => normalizeSize(size) === String(auSize));
      if (exact) return exact;

      const alphaByAu = {
        4: ['XXS', '2XS'],
        6: ['XS'],
        8: ['S'],
        10: ['M'],
        12: ['L'],
        14: ['XL'],
        16: ['2XL', 'XXL'],
        18: ['3XL', 'XXXL'],
        20: ['4XL'],
        22: ['5XL'],
        24: ['6XL']
      };

      const alphaMatches = alphaByAu[auSize] || [];
      const alpha = availableSizes.find((size) => alphaMatches.includes(normalizeSize(size)));
      return alpha || `AU ${auSize}`;
    };

    const matchAccessorySize = (preferredLabels) => {
      if (!availableSizes.length) return preferredLabels[0];

      const normalizedPreferred = preferredLabels.map(normalizeSize);
      const match = availableSizes.find((size) => normalizedPreferred.includes(normalizeSize(size)));
      return match || availableSizes[availableSizes.length - 1] || preferredLabels[0];
    };

    const extractShoeSizeNumber = (value) => {
      const match = String(value || '').replace(',', '.').match(/\d+(?:\.\d+)?/);
      return match ? Number.parseFloat(match[0]) : null;
    };

    const getProductFootwearChart = () => {
      const rawChart = productContext.shoeSizeChart;
      const rows = Array.isArray(rawChart) ? rawChart : rawChart?.rows;
      if (!Array.isArray(rows)) return [];

      return rows
        .map((row) => ({
          size: String(row.size ?? row.label ?? row.au ?? '').trim(),
          maxFootLengthCm: Number(
            row.maxFootLengthCm ??
            row.footLengthCm ??
            row.foot_length_cm ??
            row.max_cm ??
            row.cm
          )
        }))
        .filter((row) => row.size && Number.isFinite(row.maxFootLengthCm) && row.maxFootLengthCm > 0)
        .sort((a, b) => a.maxFootLengthCm - b.maxFootLengthCm);
    };

    const matchAvailableShoeSize = (recommendedSize) => {
      if (!availableSizes.length) return recommendedSize;

      const recommendedNumber = extractShoeSizeNumber(recommendedSize);
      if (!Number.isFinite(recommendedNumber)) return recommendedSize;

      const parsedAvailable = availableSizes
        .map((label) => ({ label, number: extractShoeSizeNumber(label) }))
        .filter((item) => Number.isFinite(item.number))
        .sort((a, b) => a.number - b.number);

      if (!parsedAvailable.length) return recommendedSize;

      const exact = parsedAvailable.find((item) => Math.abs(item.number - recommendedNumber) < 0.01);
      if (exact) return exact.label;

      // If the exact half/whole size is unavailable, move up to the next available size.
      const nextLarger = parsedAvailable.find((item) => item.number > recommendedNumber);
      return nextLarger?.label || parsedAvailable[parsedAvailable.length - 1].label;
    };

    const getMeasurements = () => {
      const values = {};
      measureInputs.forEach((input) => {
        const raw = Number.parseFloat(input.value);
        if (Number.isFinite(raw) && raw > 0) {
          values[input.dataset.measure] = toCm(raw);
        }
      });
      return values;
    };

    const renderResult = ({ size, details = [], reason, note }) => {
      resultSize.textContent = formatMarketSize(size);
      resultDetails.innerHTML = details.map((detail) => `
        <div class="asg-result-detail">
          <small>${detail.label}</small>
          <strong>${detail.value}</strong>
        </div>
      `).join('');
      resultReason.textContent = reason;
      resultNote.textContent = note;
      warningBox.classList.remove('is-visible');
      resultBox.classList.add('is-visible');
      resultBox.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    };

    const recommendAccessory = (type, measurements) => {
      if (type === 'sun_hats') {
        const head = measurements.head;
        const labels = head <= 54 ? ['S', 'Small'] : head <= 57 ? ['M', 'Medium'] : head <= 60 ? ['L', 'Large'] : ['XL', 'Extra Large'];
        return {
          size: matchAccessorySize(labels),
          reason: 'Based on your head circumference.',
          note: 'If the hat is adjustable, use this as your starting point and fine-tune the fastening.'
        };
      }

      if (type === 'arm_sleeves') {
        const bicep = measurements.bicep;
        const labels = bicep <= 27 ? ['S', 'Small'] : bicep <= 31 ? ['M', 'Medium'] : bicep <= 35 ? ['L', 'Large'] : ['XL', 'Extra Large'];
        return {
          size: matchAccessorySize(labels),
          reason: 'Based on the circumference of the fullest part of your upper arm.',
          note: 'The sleeve should feel secure without restricting movement.'
        };
      }

      const palm = measurements.palm;
      const labels = palm <= 17 ? ['S', 'Small'] : palm <= 19 ? ['M', 'Medium'] : palm <= 21 ? ['L', 'Large'] : ['XL', 'Extra Large'];
      return {
        size: matchAccessorySize(labels),
        reason: 'Based on your palm circumference.',
        note: 'Choose the larger size if you are between measurements or prefer less compression.'
      };
    };

    const recommendFootwear = (measurements) => {
      const footLength = measurements.foot_length;
      const productChart = getProductFootwearChart();
      const chart = productChart.length ? productChart : defaultFootwearChart;
      const row = chart.find((item) => footLength <= item.maxFootLengthCm);

      if (!row) {
        showWarning('Your foot length is above the available footwear chart. Please check the product size table or contact us for sizing help.');
        return null;
      }

      return {
        size: matchAvailableShoeSize(row.size),
        reason: 'Based on the length of your longer foot.',
        note: productChart.length
          ? 'This recommendation uses the product-specific footwear size chart. If you are between sizes, choose the larger size.'
          : 'This recommendation uses the standard women’s footwear chart. If the product has its own supplier chart or you are between sizes, choose the larger size.'
      };
    };

    const recommend = () => {
      clearMessages();

      const config = typeConfig[currentType];
      if (!config) {
        showWarning('Select a product type first.');
        return;
      }

      if (config.noSize) {
        renderResult({
          size: availableSizes[0] || 'No size required',
          reason: 'This product does not require body measurements.',
          note: 'You can continue with the product option shown on the product page.'
        });
        return;
      }

      if (config.oneSize) {
        renderResult({
          size: availableSizes[0] || 'One Size',
          reason: 'This product is designed as a one-size or adjustable item.',
          note: 'Check the product-specific dimensions and Size & Fit notes for expected coverage.'
        });
        return;
      }

      const measurements = getMeasurements();
      const missing = config.required.filter((measurement) => !measurements[measurement]);

      if (missing.length) {
        showWarning(`Enter your ${missing.map((item) => measurementLabels[item].toLowerCase()).join(' and ')} to continue.`);
        root.querySelector(`[data-measure="${missing[0]}"]`)?.focus();
        return;
      }

      const details = [...config.required, ...config.optional]
        .filter((measurement) => measurements[measurement])
        .map((measurement) => ({
          label: measurementLabels[measurement],
          value: `${formatValue(fromCm(measurements[measurement]))} ${currentUnit}`
        }));

      if (config.footwear) {
        const footwearResult = recommendFootwear(measurements);
        if (footwearResult) renderResult({ ...footwearResult, details });
        return;
      }

      if (config.accessory) {
        const accessoryResult = recommendAccessory(currentType, measurements);
        renderResult({ ...accessoryResult, details });
        return;
      }

      let chartIndex = auChart.findIndex((row) =>
        config.primary.every((measurement) => measurements[measurement] <= row[measurement])
      );

      if (chartIndex === -1) {
        showWarning(`Your measurements are above the current ${formatMarketSize('AU 24', currentType)} chart. Please contact us for personal sizing help.`);
        return;
      }

      if (fitSelect?.value === 'relaxed') {
        chartIndex = Math.min(chartIndex + 1, auChart.length - 1);
      }

      const row = auChart[chartIndex];
      const size = matchAvailableSize(row.au);
      const mainMeasurements = config.primary.map((measurement) => measurementLabels[measurement].toLowerCase()).join(', ');

      renderResult({
        size,
        details,
        reason: `Based on your ${mainMeasurements} and a ${fitSelect?.value || 'regular'} fit preference.`,
        note: `Your body-chart recommendation is ${formatMarketSize(`AU ${row.au}`, currentType)}. Product fabrics and individual cuts can vary, so also check the product-specific Size & Fit notes.`
      });
    };

    const setUnit = (nextUnit) => {
      if (nextUnit === currentUnit) return;

      measureInputs.forEach((input) => {
        const value = Number.parseFloat(input.value);
        if (!Number.isFinite(value) || value <= 0) return;

        const valueCm = currentUnit === 'in' ? value * 2.54 : value;
        input.value = formatValue(nextUnit === 'in' ? valueCm / 2.54 : valueCm);
      });

      currentUnit = nextUnit;
      unitButtons.forEach((button) => {
        button.classList.toggle('is-active', button.dataset.asgUnit === currentUnit);
      });
      unitLabels.forEach((label) => {
        label.textContent = currentUnit === 'cm' ? '(cm)' : '(in)';
      });

      clearMessages();
    };

    const reset = () => {
      measureInputs.forEach((input) => {
        input.value = '';
      });
      if (fitSelect) fitSelect.value = 'regular';
      setUnit('cm');
      typeSelect.value = detectProductType();
      typeField.hidden = true;
      applyType(typeSelect.value, { automatic: true });
      clearMessages();
    };

    unitButtons.forEach((button) => {
      button.addEventListener('click', () => setUnit(button.dataset.asgUnit));
    });

    typeSelect?.addEventListener('change', () => {
      applyType(typeSelect.value, { automatic: false });
    });

    changeTypeButton?.addEventListener('click', () => {
      typeField.hidden = false;
      typeSelect?.focus();
    });

    recommendButton?.addEventListener('click', recommend);
    resetButton?.addEventListener('click', reset);

    measureInputs.forEach((input) => {
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          recommend();
        }
      });
    });

    const applyProductContext = (nextContext) => {
      productContext = nextContext && typeof nextContext === 'object' ? nextContext : {};
      contextSearchText = buildContextSearchText(productContext);
      availableSizes = (productContext.availableSizes || []).map((size) => String(size).trim());

      measureInputs.forEach((input) => {
        input.value = '';
      });
      if (fitSelect) fitSelect.value = 'regular';
      if (currentUnit !== 'cm') setUnit('cm');

      const detectedType = detectProductType();
      if (typeSelect) typeSelect.value = detectedType;
      if (typeField) typeField.hidden = true;
      applyType(detectedType, { automatic: true });
      clearMessages();
    };

    dialogContent.addEventListener('aurelia:size-guide-context', (event) => {
      applyProductContext(event.detail);
    });

    localizeStaticSizeGuide();
    applyProductContext(productContext);
  };

  const SELECTOR = '[data-asg-dialog-content]';
  const initAll = (scope = document) => {
    if (scope instanceof Element && scope.matches(SELECTOR)) initSizeGuide(scope);
    scope.querySelectorAll?.(SELECTOR).forEach(initSizeGuide);
  };

  window.AureliaSizeGuide = { init: initSizeGuide, initAll };

  // Context updates (e.g. from the bundle buy box) can reach a dialog that was
  // injected later (quick add, section rendering). Capture-phase listeners see
  // non-bubbling events too, so initialise just in time before delivery.
  document.addEventListener(
    'aurelia:size-guide-context',
    (event) => {
      const target = event.target;
      if (target instanceof Element && target.matches(SELECTOR)) initSizeGuide(target);
    },
    true
  );

  // Dialogs added after page load (quick add modal, morphing) are initialised
  // on the first interaction instead of re-running inline scripts.
  document.addEventListener('click', () => initAll(), { capture: true, passive: true });
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }
})();
